/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useRef, useState, useEffect, forwardRef, useMemo } from "react";
import { PiUsers } from "react-icons/pi";
import { IoCloseOutline } from "react-icons/io5";
import InputText from "../input/InputText";
import InputSelect from "../input/InputSelect";
import useCollector from "../../hooks/useCollector";
import SearchableDropdown2 from "../input/SearchableDropdown2";
import { getState, getLgaByState } from "../../ds/resource";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import toast from "react-hot-toast";
import { Formik, Form } from "formik";
import * as yup from "yup";
import {
  createCollectorSchema,
  createCollectorSchemaTwo,
  createCollectorTwoSchema,
} from "../../utils/validationSchema/collectorSchema";
import { useSelector } from "react-redux";
import {
  checkCollectorNameExists,
  getCollectorNamesByPhone,
} from "../../ds/collectors";
import debounce from "lodash.debounce";

export const CollectorModal = ({
  closeModal,
  requestType,
  aggregatorList,
  getCollectors,
  userData,
  states,
  lgas,
  setSelectedStateId,
  isAggregator,
  selectOptDefault,
}) => {
  const modalRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const { createNewCollector, updateExistingCollector } = useCollector();
  const [nameCheck, setNameCheck] = useState(null);
  const [lastNameCheck, setLastNameCheck] = useState(null);
  const [phoneCheck, setPhoneCheck] = useState(null);
  const [showPhoneConfirm, setShowPhoneConfirm] = useState(false);
  const [pendingSubmitData, setPendingSubmitData] = useState(null);

  //for date range
  const [startDate, setStartDate] = useState("");

  const { program } = useSelector((state) => state.auth);

  // Create a validation schema that conditionally requires fields based on program and user type
  const getValidationSchema = () => {
    let baseSchema;
    
    if (isAggregator && program !== 2 && program !== 3) {
      baseSchema = createCollectorSchemaTwo;
    } else if (program === 2 || program === 3) {
      baseSchema = createCollectorTwoSchema;
    } else if (!isAggregator && program !== 2 && program !== 3) {
      baseSchema = createCollectorSchema;
    } else {
      baseSchema = createCollectorSchema;
    }

    // Create conditional schema
    return baseSchema.shape({
      // ageRange is only required for programs other than 1
      ageRange: program === 1 
        ? yup.string().optional()
        : yup.string().required("Age range is required"),
      
      // dateOfBirth is only required for program === 1
      dateOfBirth: program === 1
        ? yup.string().required("Date of birth is required")
        : yup.string().optional(),
      
      // aggregatorId is not required for aggregators (they have a default value)
      aggregatorId: isAggregator
        ? yup.string().optional()
        : yup.string().required("Aggregator is required")
    });
  };

  const ExampleCustomInput = forwardRef(({ value, onClick }, ref) => (
    <div className="flex flex-col">
      <label className={`after:content-['*'] after:text-red-500`}>
        Date of Birth
      </label>
      <div
        className="example-custom-input border border-gray-300 h-[44px] w-[140px] md:w-[200px] lg:w-[288px] p-2 rounded-md"
        onClick={onClick}
        ref={ref}
      >
        <h3>{value ? value : "Select date"}</h3>
      </div>
    </div>
  ));
  const ExampleCustomInputTwo = forwardRef(({ value, onClick }, ref) => (
    <div className="flex flex-col">
      <label className={`after:content-['*'] after:text-red-500`}>
        Enrollment Date
      </label>
      <div
        className="example-custom-input border border-gray-300 h-[44px] w-[140px] md:w-[200px] lg:w-[288px] p-2 rounded-md"
        onClick={onClick}
        ref={ref}
      >
        <h3>{value ? value : "Select date"}</h3>
      </div>
    </div>
  ));

  const initialValues = {
    address: requestType === "edit" ? userData.address : "",
    firstName: requestType === "edit" ? userData.firstName : "",
    lastName: requestType === "edit" ? userData.lastName : "",
    phoneNumber: requestType === "edit" ? userData.phoneNumber : "",
    email: requestType === "edit" ? userData.email : "",
    location: requestType === "edit" ? userData.location : "",
    state: requestType === "edit" ? userData.state : "",
    gender: requestType === "edit" ? userData.gender : "",
    dateOfBirth: requestType === "edit" ? userData.dateOfBirth : "",
    aggregatorId: requestType === "edit" ? selectOptDefault.value : "",
    disabilityStatus: requestType === "edit" ? userData.disabilityStatus : "",
    baselineIncomeMonthly:
      requestType === "edit" ? userData.baselineIncomeMonthly : 0,
    isCurrentIncomeHigher:
      requestType === "edit" ? userData.isCurrentIncomeHigher : "",
    isCollectorJobMoreStable:
      requestType === "edit" ? userData.isCollectorJobMoreStable : "",
    ageRange: requestType === "edit" ? userData.ageRange : "",
    existingCollector: requestType === "edit" ? userData.existingCollector : "",
    institution: requestType === "edit" ? userData.institution : "",
    enrollmentDate: requestType === "edit" ? userData.enrollmentDate : "",
    enrollmentChannel: requestType === "edit" ? userData.enrollmentChannel : "",
    employmentType: requestType === "edit" ? userData.employmentType : "",
    plasticCollectedKgPerMonth:
      requestType === "edit" ? userData.plasticCollectedKgPerMonth : "",
    hasBankAccount: requestType === "edit" ? userData.hasBankAccount : "",
    savesMoneyRegularly:
      requestType === "edit" ? userData.savesMoneyRegularly : "",
    experiencedHarassmentOrSafetyConcerns:
      requestType === "edit"
        ? userData.experiencedHarassmentOrSafetyConcerns
        : "",
    childrenShouldWorkAsCollectors:
      requestType === "edit" ? userData.childrenShouldWorkAsCollectors : "",
    partOfCollectionGroup:
      requestType === "edit" ? userData.partOfCollectionGroup : "",
    clusterName: requestType === "edit" ? userData.clusterName : "",
    clusterGenderCategory:
      requestType === "edit" ? userData.clusterGenderCategory : "",
    wouldJoinWomenOnlyCluster:
      requestType === "edit" ? userData.wouldJoinWomenOnlyCluster : "",
  };

  const submitCollector = async (data) => {
    setLoading(true);
    try {
      if (requestType === "edit") {
        setLoading(true);
        const res = await updateExistingCollector({
          ...data,
          id: userData.id,
          programId: program,
          aggregatorId: isAggregator
            ? selectOptDefault?.value
            : data.aggregatorId,
        });
        if (res.code === "00") {
          toast.success("Collector updated");
          closeModal();
        }
        if (res.code !== "00") {
          setLoading(false);
          toast.error(res.message || "Error updating collector");
        }
      }
      if (requestType !== "edit") {
        setLoading(true);
        const res = await createNewCollector({
          ...data,
          programId: program,
          aggregatorId: isAggregator
            ? selectOptDefault?.value
            : data.aggregatorId,
        });
        if (res.code === "00") {
          toast.success("Collector created");
          closeModal();
        }
        if (res.code !== "00") {
          setLoading(false);
          toast.error(res.message || "Error creating Collector");
        }
      }
    } catch (error) {
      toast.error(error.message || "something went wrong");
    } finally {
      setLoading(false);
      getCollectors();
    }
  };

  const onSubmit = async (data) => {
    if (phoneCheck?.data?.length > 0) {
      setPendingSubmitData(data);
      setShowPhoneConfirm(true);
      return;
    }

    await submitCollector(data);
  };

  const handleConfirmPhoneSubmit = async () => {
    setShowPhoneConfirm(false);
    if (pendingSubmitData) {
      await submitCollector(pendingSubmitData);
      setPendingSubmitData(null);
    }
  };

  const handleCancelPhoneConfirm = () => {
    setShowPhoneConfirm(false);
    setPendingSubmitData(null);
  };

  const debouncedNameCheck = useMemo(
    () =>
      debounce(async (value) => {
        if (!value) return;

        const nameRes = await checkCollectorNameExists(value);

        setNameCheck(nameRes);

        // if (nameRes?.data) {
        //   toast.error("Name already exist");
        // }
      }, 1000),
    [],
  );

  const debouncedLastNameCheck = useMemo(
    () =>
      debounce(async (value) => {
        if (!value) return;

        const nameRes = await checkCollectorNameExists(value);

        setLastNameCheck(nameRes);
      }, 1000),
    [],
  );

  const debouncedPhoneCheck = useMemo(
    () =>
      debounce(async (value) => {
        if (!value || value.length !== 11) {
          setPhoneCheck(null);
          return;
        }

        const phoneRes = await getCollectorNamesByPhone(value);

        setPhoneCheck(phoneRes);
      }, 1000),
    [],
  );

  useEffect(() => {
    return () => {
      debouncedNameCheck.cancel();
      debouncedLastNameCheck.cancel();
      debouncedPhoneCheck.cancel();
    };
  }, [debouncedNameCheck, debouncedLastNameCheck, debouncedPhoneCheck]);


  return (
    <>
    <Formik
      initialValues={initialValues}
      validationSchema={getValidationSchema()}
      onSubmit={onSubmit}
    >
      {({ handleChange, errors, values, handleSubmit, setFieldValue }) => {
        return (
          <Form>
            <div style={{ height: "500px" }}>
              <div className="flex flex-col gap-y-2 min-w-[300px] md:min-w-[450px] lg:w-[640px] bg-white p-4 h-max">
                <div className="flex justify-between">
                  <PiUsers style={{ width: 26, height: 26 }} />
                  <IoCloseOutline
                    style={{ width: 26, height: 26, cursor: "pointer" }}
                    onClick={() => closeModal()}
                  />
                </div>
                <div className="">
                  <h1 className="capitalize font-bold">
                    {requestType === "edit"
                      ? "Edit Collector"
                      : "Create new Collector"}
                  </h1>
                  <p className="text-sm">Enter the details below</p>
                </div>
                <div className="flex justify-between gap-2">
                  <div>
                    <InputText
                      label={"FirstName"}
                      placeholder={"Enter first name"}
                      name={"firstName"}
                      fieldRequired={true}
                      error={errors.firstName}
                      value={values.firstName}
                      handleChange={(e) => {
                        const value = e.target.value;

                        // let formik (or your form lib) update immediately
                        handleChange(e);

                        // debounce ONLY the async check
                        debouncedNameCheck(value);
                      }}
                    />
                    {nameCheck?.data && (
                      <p className="text-red-600">Name already exist</p>
                    )}
                  </div>
                  <div>
                    <InputText
                      label={"LastName"}
                      error={errors.lastName}
                      name={"lastName"}
                      fieldRequired={true}
                      placeholder={"Enter last name"}
                      value={values.lastName}
                      handleChange={(e) => {
                        const value = e.target.value;

                        handleChange(e);

                        debouncedLastNameCheck(value);
                      }}
                    />
                    {lastNameCheck?.data && (
                      <p className="text-red-600">Name already exist</p>
                    )}
                  </div>
                </div>
                <div className="flex justify-between w-full gap-2">
                  <div>
                    <InputText
                      label={"Phone Number"}
                      placeholder={"Enter phone number"}
                      fieldRequired={true}
                      className={`${isAggregator ? "w-full" : ""}`}
                      textStyle={`${isAggregator ? "w-full" : ""}`}
                      name={"phoneNumber"}
                      value={values.phoneNumber}
                      error={errors.phoneNumber}
                      handleChange={(e) => {
                        const value = e.target.value;

                        handleChange(e);

                        debouncedPhoneCheck(value);
                      }}
                    />
                    {phoneCheck?.data?.length > 0 && (
                      <p className="text-red-600">
                        Record exist with this number
                      </p>
                    )}
                  </div>
                  {!isAggregator ? (
                  <SearchableDropdown2
                    label={"Aggregator"}
                    options={aggregatorList || []}
                    name="aggregatorId"
                    error={errors.aggregatorId}
                    dropDownStyle={{
                      display: isAggregator ? "none" : "block",
                    }}
                    // selectedOption={isAggregator ? selectOptDefault?.value : ""}
                    // selectedOption={selectOptDefault?.value}
                    // className={"hidden"}
                    // defaultInputValue={values.aggregatorId}
                    fieldRequired={true}
                    defaultValue={
                      requestType === "edit" ? [selectOptDefault] : []
                    }
                    placeholder="Select aggregator"
                    handleChange={(selectionOption) => {
                      setFieldValue("aggregatorId", selectionOption.value);
                    }}
                  />
                  ) : (
                    ""
                  )}
                </div>
                <div className="flex justify-between w-full gap-2">
                  <InputText
                    label={"Email Address"}
                    placeholder={"Enter email address"}
                    name={"email"}
                    // fieldRequired={true}
                    value={values.email}
                    error={errors.email}
                    handleChange={handleChange}
                  />
                  <InputSelect
                    label={"Disability Status"}
                    placeholder="Select disability status"
                    options={["yes", "no"]}
                    fieldRequired={true}
                    name="disabilityStatus"
                    error={errors.disabilityStatus}
                    handleChange={handleChange}
                  />
                </div>
                <div className="w-full flex justify-between gap-2">
                  <InputText
                    label={"Address"}
                    placeholder={"Enter address"}
                    name={"address"}
                    textStyle={"sm:mt-6"}
                    value={values.address}
                    fieldRequired={true}
                    error={errors.address}
                    handleChange={handleChange}
                  />
                  <InputText
                    label={"Baseline Income Monthly(NGN)"}
                    placeholder={"Enter address"}
                    name={"baselineIncomeMonthly"}
                    value={values.baselineIncomeMonthly}
                    // fieldRequired={true}
                    error={errors.baselineIncomeMonthly}
                    handleChange={handleChange}
                  />
                </div>
                <div className="flex justify-between w-full">
                  <SearchableDropdown2
                    label={"State"}
                    options={states || []}
                    name="state"
                    error={errors.state}
                    fieldRequired={true}
                    placeholder="Select state"
                    defaultInputValue={values.state || ""}
                    handleChange={(selectionOption) => {
                      setFieldValue("state", selectionOption?.label);
                      setSelectedStateId(selectionOption?.value);
                    }}
                  />

                  <SearchableDropdown2
                    label={"Lga"}
                    options={lgas || []}
                    name="location"
                    error={errors.location}
                    fieldRequired={true}
                    defaultInputValue={values.location || ""}
                    placeholder="Select lga"
                    handleChange={(selectionOption) =>
                      setFieldValue("location", selectionOption?.label)
                    }
                  />
                </div>
                <div className="flex justify-between gap-2">
                  <InputSelect
                    label={"Gender"}
                    placeholder="Select gender"
                    name="gender"
                    fieldRequired={true}
                    value={values.gender}
                    error={errors.gender}
                    options={["Male", "Female", "Mixed"]}
                    handleChange={handleChange}
                  />
                  {program === 1 ? (
                    <div className="flex flex-col">
                      <DatePicker
                        selected={
                          values.dateOfBirth ? values.dateOfBirth : startDate
                        }
                        onChange={(date) => {
                          setStartDate(date);
                          setFieldValue(
                            "dateOfBirth",
                            moment(date).format("YYYY-MM-DD"),
                          );
                        }}
                        customInput={<ExampleCustomInput />}
                        showYearDropdown
                        yearDropdownItemNumber={100}
                        scrollableYearDropdown
                      />
                      {/* {errors.dateOfBirth ? (
                      <p className="text-red-500">{errors.dateOfBirth}</p>
                    ) : (
                      ""
                    )} */}
                    </div>
                  ) : (
                    " "
                  )}
                  {program === 1 ? (
                    ""
                  ) : (
                    <SearchableDropdown2
                      label={"Age Range"}
                      options={
                        program === 3
                          ? [
                              {
                                label: "18-35",
                                value: "18-35",
                              },
                              {
                                label: "36+",
                                value: "36+",
                              },
                            ]
                          : [
                              {
                                label: "15-29",
                                value: "15-29",
                              },
                              {
                                label: "30+",
                                value: "30+",
                              },
                              {
                                label: "Mixed",
                                value: "Mixed",
                              },
                            ]
                      }
                      name="ageRange"
                      error={errors.ageRange}
                      fieldRequired={true}
                      placeholder="Select age range"
                      defaultInputValue={values.ageRange || ""}
                      handleChange={(selectionOption) => {
                        setFieldValue("ageRange", selectionOption?.value);
                      }}
                    />
                  )}
                </div>
                <div className="flex justify-between w-full">
                  <SearchableDropdown2
                    label={"Is this an Existing/New Collector?"}
                    options={[
                      {
                        label: "New",
                        value: "New",
                      },
                      {
                        label: "Existing",
                        value: "Existing",
                      },
                    ]}
                    name="existingCollector"
                    error={errors.existingCollector}
                    fieldRequired={true}
                    placeholder="Select existing"
                    defaultInputValue={values.existingCollector || ""}
                    handleChange={(selectionOption) => {
                      setFieldValue(
                        "existingCollector",
                        selectionOption?.value,
                      );
                    }}
                  />

                  <InputSelect
                    label={"Is this an institution?"}
                    placeholder="Select option"
                    name="institution"
                    fieldRequired={true}
                    value={values.institution}
                    error={errors.institution}
                    options={["Yes", "No"]}
                    handleChange={(e) => {
                      setFieldValue("institution", e.target.value);
                    }}
                  />
                </div>
                <div className="flex justify-between gap-2">
                  <div className="flex flex-col">
                    <DatePicker
                      selected={
                        values.enrollmentDate
                          ? values.enrollmentDate
                          : startDate
                      }
                      onChange={(date) => {
                        setStartDate(date);
                        setFieldValue(
                          "enrollmentDate",
                          moment(date).format("YYYY-MM-DD"),
                        );
                      }}
                      customInput={<ExampleCustomInputTwo />}
                      showYearDropdown
                      yearDropdownItemNumber={100}
                      scrollableYearDropdown
                    />
                    {/* {errors.dateOfBirth ? (
                      <p className="text-red-500">{errors.dateOfBirth}</p>
                    ) : (
                      ""
                    )} */}
                  </div>
                  <SearchableDropdown2
                    label={"Enrollment Channel"}
                    options={[
                      {
                        label: "LCT",
                        value: "LCT",
                      },
                      {
                        label: "Aggregator",
                        value: "Aggregator",
                      },
                      {
                        label: "Others",
                        value: "Others",
                      },
                    ]}
                    name="enrollmentChannel"
                    error={errors.enrollmentChannel}
                    fieldRequired={true}
                    placeholder="Select existing"
                    defaultInputValue={values.enrollmentChannel || ""}
                    handleChange={(selectionOption) => {
                      setFieldValue(
                        "enrollmentChannel",
                        selectionOption?.label,
                      );
                    }}
                  />
                </div>
                <div className="flex justify-between` w-full  gap-2">
                  <InputText
                    label={"How many KG of plastics do you collect per month"}
                    placeholder={"Enter amount in KG"}
                    name={"plasticCollectedKgPerMonth"}
                    value={values.plasticCollectedKgPerMonth}
                    error={errors.plasticCollectedKgPerMonth}
                    fieldRequired={true}
                    handleChange={handleChange}
                  />
                  <InputSelect
                    label={"Are you a full-time or part-time collector"}
                    placeholder="Select option"
                    name="employmentType"
                    value={values.employmentType}
                    error={errors.employmentType}
                    fieldRequired={true}
                    options={["Full-time", "Part-time"]}
                    defaultInputValue={values.employmentType || ""}
                    handleChange={(e) => {
                      setFieldValue("employmentType", e.target.value);
                    }}
                  />
                </div>
                <div className="flex justify-between w-full gap-2">
                  <InputSelect
                    label={"Do you currently have a bank account"}
                    placeholder="Select option"
                    name="hasBankAccount"
                    value={values.hasBankAccount}
                    error={errors.hasBankAccount}
                    fieldRequired={true}
                    options={["Yes", "No"]}
                    handleChange={(e) => {
                      setFieldValue("hasBankAccount", e.target.value);
                    }}
                  />
                  <InputSelect
                    label={"Do you currently save money regularly"}
                    placeholder="Select option"
                    name="savesMoneyRegularly"
                    value={values.savesMoneyRegularly}
                    error={errors.savesMoneyRegularly}
                    fieldRequired={true}
                    options={["Yes", "No"]}
                    handleChange={(e) => {
                      setFieldValue("savesMoneyRegularly", e.target.value);
                    }}
                  />
                </div>
                <div className="flex justify-between w-full gap-2">
                  <InputSelect
                    label={
                      "Have you ever experienced harassment or safety concerns while collecting"
                    }
                    placeholder="Select option"
                    name="experiencedHarassmentOrSafetyConcerns"
                    value={values.experiencedHarassmentOrSafetyConcerns}
                    error={errors.experiencedHarassmentOrSafetyConcerns}
                    fieldRequired={true}
                    options={["Yes", "No"]}
                    handleChange={(e) => {
                      setFieldValue(
                        "experiencedHarassmentOrSafetyConcerns",
                        e.target.value,
                      );
                    }}
                  />
                  <InputSelect
                    label={
                      "Do you believe children under 18 should work as waste collectors"
                    }
                    placeholder="Select option"
                    name="childrenShouldWorkAsCollectors"
                    value={values.childrenShouldWorkAsCollectors}
                    error={errors.childrenShouldWorkAsCollectors}
                    fieldRequired={true}
                    options={["Yes", "No"]}
                    handleChange={(e) => {
                      setFieldValue(
                        "childrenShouldWorkAsCollectors",
                        e.target.value,
                      );
                    }}
                  />
                </div>
                <div className="flex justify-between w-full gap-2">
                  <InputSelect
                    label={
                      "Are you currently part of a collection group or cluster"
                    }
                    placeholder="Select option"
                    name="partOfCollectionGroup"
                    value={values.partOfCollectionGroup}
                    error={errors.partOfCollectionGroup}
                    fieldRequired={true}
                    options={["Yes", "No"]}
                    handleChange={(e) => {
                      setFieldValue("partOfCollectionGroup", e.target.value);
                    }}
                  />
                  {values.partOfCollectionGroup &&
                    values.partOfCollectionGroup.toLowerCase() === "yes" && (
                      <InputText
                        label={"If yes, what is the name of the cluster"}
                        placeholder={"Enter cluster name"}
                        name={"clusterName"}
                        value={values.clusterName}
                        error={errors.clusterName}
                        handleChange={handleChange}
                      />
                    )}
                </div>
                <div className="flex justify-between w-full gap-2">
                  {values.partOfCollectionGroup &&
                    values.partOfCollectionGroup.toLowerCase() === "yes" && (
                      <InputSelect
                        label={
                          "Which gender category does this cluster belong to"
                        }
                        placeholder="Select option"
                        name="clusterGenderCategory"
                        value={values.clusterGenderCategory}
                        error={errors.clusterGenderCategory}
                        options={["Men-only", "Women-only", "Mixed-gender"]}
                        handleChange={(e) => {
                          setFieldValue(
                            "clusterGenderCategory",
                            e.target.value,
                          );
                        }}
                      />
                    )}
                  {values.gender === "Female" && (
                    <InputSelect
                      label={
                        "(If female) Would you like to join a women-only cluster"
                      }
                      placeholder="Select option"
                      name="wouldJoinWomenOnlyCluster"
                      value={values.wouldJoinWomenOnlyCluster}
                      error={errors.wouldJoinWomenOnlyCluster}
                      options={["Yes", "No"]}
                      handleChange={(e) => {
                        setFieldValue(
                          "wouldJoinWomenOnlyCluster",
                          e.target.value,
                        );
                      }}
                    />
                  )}
                </div>
              

                <br />
                <div className="flex gap-2 justify-center">
                  <button
                    type="button"
                    className="mx-auto px-2 w-full h-[40px] font-normal text-xs flex justify-center items-center gap-3 disabled:cursor-not-allowed border border-pfBlack md:w-272"
                    onClick={() => closeModal()}
                  >
                    Cancel
                  </button>

                  {requestType === "edit" ? (
                    <button
                      type="submit"
                      disabled={loading}
                      className="bg-green-700 text-white flex justify-center items-center h-[40px] w-full gap-2"
                    >
                      {loading ? "Updating..." : "Save changes"}
                    </button>
                  ) : (
                    <button
                      type="submit"
                      disabled={loading}
                      className="bg-green-700 text-white flex justify-center items-center h-[40px] w-full gap-2"
                    >
                      {loading ? "Creating..." : "Create Collector"}
                    </button>
                 
                  )}
                </div>
              </div>
            </div>
          </Form>
        );
      }}
    </Formik>
    {showPhoneConfirm && (
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50">
        <div className="flex flex-col gap-y-3 w-[400px] bg-white p-4">
          <p className="text-center">
            The following collectors already exist with the phone number.
          </p>
          <ul className="list-disc pl-6 text-sm">
            {phoneCheck?.data?.map((collector, index) => (
              <li key={`${collector?.name}-${index}`}>
                {collector?.name}
              </li>
            ))}
          </ul>
          
          <div className="flex gap-2 justify-center">
            <button
              type="button"
              className="mx-auto px-2 w-full h-[40px] font-normal text-xs flex justify-center items-center gap-3 disabled:cursor-not-allowed border border-pfBlack md:w-272"
              onClick={handleCancelPhoneConfirm}
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={loading}
              onClick={handleConfirmPhoneSubmit}
              className="bg-green-700 text-white flex justify-center items-center h-[40px] w-full gap-2"
            >
              {loading ? "Submitting..." : "Submit"}
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};
